/* =============================================================
   ROUTER
   =============================================================
   The whole route tree, in one place:

     AuthLayout  (requiresAnonymous) → sign-in, sign-up
     AppLayout   (requiresAuth)      → every screen in the shell
     *                               → 404

   Adding a screen = add a record here and an entry in
   src/config/nav.config.ts. Nothing else needs to know about it.

   Both layouts sit at `path: '/'` with absolutely-pathed children,
   which is how Vue Router expresses "a layout that owns a set of
   top-level URLs" without those URLs gaining a prefix.
   ============================================================= */

import { watch } from 'vue'
import { createRouter, createWebHistory } from 'vue-router'
import AppLayout from '@/layouts/AppLayout.vue'
import AuthLayout from '@/layouts/AuthLayout.vue'
import { HOME_ROUTE, patterns, routes } from '@/config/routes'
import { authReady, useAuth } from '@/lib/auth/useAuth'
import type { LocationQueryValue } from 'vue-router'
import AvailabilityPage from '@/pages/AvailabilityPage.vue'
import BookingsPage from '@/pages/BookingsPage.vue'
import DashboardPage from '@/pages/DashboardPage.vue'
import LocationsPage from '@/pages/LocationsPage.vue'
import NotFoundPage from '@/pages/NotFoundPage.vue'
import ReportsPage from '@/pages/ReportsPage.vue'
import SearchPage from '@/pages/SearchPage.vue'
import SettingsPage from '@/pages/SettingsPage.vue'
import VesselsPage from '@/pages/VesselsPage.vue'
import VesselFormPage from '@/pages/VesselFormPage.vue'
import VesselDetailPage from '@/pages/VesselDetailPage.vue'
import ComparePage from '@/pages/ComparePage.vue'
import BerthProfilePage from '@/pages/BerthProfilePage.vue'
import BookPage from '@/pages/BookPage.vue'
import LocationFormPage from '@/pages/LocationFormPage.vue'
import LocationDetailPage from '@/pages/LocationDetailPage.vue'
import RateFormPage from '@/pages/RateFormPage.vue'
import BlockFormPage from '@/pages/BlockFormPage.vue'
import BookingDetailPage from '@/pages/BookingDetailPage.vue'
import BookingConfirmationPage from '@/pages/BookingConfirmationPage.vue'
import SignInPage from '@/pages/auth/SignInPage.vue'

const { status } = useAuth()

export const router = createRouter({
  history: createWebHistory(),

  routes: [
    // Authenticated — sidebar + top bar + content.
    {
      path: '/',
      component: AppLayout,
      meta: { requiresAuth: true },
      children: [
        { path: '', redirect: HOME_ROUTE },
        { path: routes.dashboard, component: DashboardPage },

        // Ship operator: find, compare, book.
        { path: routes.search, component: SearchPage },
        { path: routes.compare, component: ComparePage },
        { path: patterns.berthProfile, component: BerthProfilePage },
        { path: patterns.book, component: BookPage },

        // Berth owner: locations, their rates and blocked time.
        { path: routes.locations, component: LocationsPage },
        { path: routes.locationNew, component: LocationFormPage },
        { path: patterns.berth, component: LocationDetailPage },
        { path: patterns.berthEdit, component: LocationFormPage },
        { path: patterns.berthRateNew, component: RateFormPage },
        { path: patterns.berthRateEdit, component: RateFormPage },
        { path: patterns.berthBlockNew, component: BlockFormPage },
        { path: routes.availability, component: AvailabilityPage },

        // Fleet.
        { path: routes.vessels, component: VesselsPage },
        { path: routes.vesselNew, component: VesselFormPage },
        { path: patterns.vessel, component: VesselDetailPage },
        { path: patterns.vesselEdit, component: VesselFormPage },

        // Both sides.
        { path: routes.bookings, component: BookingsPage },
        { path: patterns.booking, component: BookingDetailPage },
        { path: patterns.bookingConfirmation, component: BookingConfirmationPage },
        { path: routes.reports, component: ReportsPage },
        { path: routes.settings, component: SettingsPage },
      ],
    },

    // Public — full-screen auth screens.
    {
      path: '/',
      component: AuthLayout,
      meta: { requiresAnonymous: true },
      children: [
        { path: routes.signIn, component: SignInPage },

        // No public sign-up: River Atlas sets accounts up by hand, and
        // sign-up is switched off in the Supabase project. Old links land
        // on sign-in.
        { path: routes.signUp, redirect: routes.signIn },
      ],
    },

    { path: '/:pathMatch(.*)*', name: 'not-found', component: NotFoundPage },
  ],

  // Land at the top of a new screen, but honour the back button.
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 }
  },
})

/**
 * Where to land after a successful sign-in. Only same-site paths are
 * honoured, so `?redirect=` cannot be used as an open redirect.
 */
function safeRedirect(target: LocationQueryValue | LocationQueryValue[]): string {
  if (typeof target !== 'string') return HOME_ROUTE
  return target.startsWith('/') && !target.startsWith('//') ? target : HOME_ROUTE
}

/**
 * The route guards. Awaiting `authReady()` first is what stops a
 * signed-in user being bounced to sign-in on a hard reload, while
 * the session is still being restored.
 */
router.beforeEach(async (to) => {
  await authReady()

  if (to.meta.requiresAuth && status.value !== 'authenticated') {
    // Remember where they were headed so sign-in can return them there.
    return { path: routes.signIn, query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresAnonymous && status.value === 'authenticated') {
    return HOME_ROUTE
  }

  return true
})

/**
 * The guard above only runs on navigation, but the session can also
 * change underneath a screen that is already mounted: a sign-out, an
 * expiry, or the same user signing in or out in another tab. Mirror
 * those into a navigation so the URL never contradicts the session.
 *
 * This is also what redirects after sign-in and sign-up — the forms
 * only have to call `signIn()` / `signUp()`.
 */
watch(status, (next) => {
  const current = router.currentRoute.value

  if (next === 'anonymous' && current.meta.requiresAuth) {
    void router.replace({ path: routes.signIn, query: { redirect: current.fullPath } })
    return
  }

  if (next === 'authenticated' && current.meta.requiresAnonymous) {
    void router.replace(safeRedirect(current.query.redirect))
  }
})
